import { ConfinedPointer } from '../../input/confined-pointer.js';

export class FullscreenControls {
  private pointer: ConfinedPointer;
  private busy = false;

  constructor(private root: HTMLElement, private button: HTMLButtonElement,
    private capture: HTMLButtonElement, private playing: () => boolean, private notice: (message: string) => void) {
    this.pointer = new ConfinedPointer(root);
    button.addEventListener('click', () => void this.toggle());
    capture.addEventListener('click', () => this.captureCursor());
    document.addEventListener('fullscreenchange', () => {
      if (document.fullscreenElement !== root) this.pointer.release();
      this.refresh();
    });
    document.addEventListener('pointerlockchange', () => {
      if (!this.busy && document.fullscreenElement !== root) this.pointer.release();
      this.refresh();
    });
    document.addEventListener('pointerlockerror', () => {
      this.notice('The browser refused cursor capture. Try Capture cursor, or open the game directly in Chrome or Edge.');
      this.refresh();
    });
    this.refresh();
  }

  releaseCursor() { this.pointer.release(); this.refresh(); }

  refresh() {
    const fullscreen = document.fullscreenElement === this.root;
    this.button.textContent = fullscreen ? 'Exit fullscreen' : 'Fullscreen';
    this.button.setAttribute('aria-pressed', String(fullscreen));
    this.button.disabled = this.busy || !document.fullscreenEnabled;
    this.button.title = document.fullscreenEnabled ? 'Fullscreen with cursor capture during play · Esc releases the cursor' : 'Fullscreen is unavailable in this browser';
    this.capture.hidden = !fullscreen || !this.playing() || this.pointer.locked;
    this.capture.disabled = this.busy || !this.root.requestPointerLock;
  }

  private captureCursor() {
    if (!this.playing() || this.pointer.locked || !this.root.requestPointerLock) return;
    try {
      // Some browsers return void; errors also arrive through pointerlockerror.
      const request = this.root.requestPointerLock();
      if (request) void request.catch(() => {});
    } catch {
      this.notice('Cursor capture was unavailable. Use middle-drag or try Capture cursor again.');
    }
  }

  private async toggle() {
    if (this.busy) return;
    this.busy = true; this.refresh();
    try {
      if (document.fullscreenElement === this.root) {
        this.pointer.release(); await document.exitFullscreen();
      } else {
        // Pointer lock must be requested before fullscreen consumes click activation.
        this.captureCursor();
        await this.root.requestFullscreen({ navigationUI: 'hide' });
      }
    } catch {
      this.pointer.release(); this.notice('Fullscreen was unavailable. Open the game in Chrome or Edge and try again.');
    } finally {
      this.busy = false;
      if (document.fullscreenElement !== this.root) this.pointer.release();
      this.refresh();
    }
  }
}
